import React, { FunctionComponent, useEffect } from 'react'
import styled from 'styled-components'
import { Route, Routes, useParams, useLocation } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import Grid from '@mui/material/Grid'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import PageContent from '../../components/PageContent'
import { colors, spacing } from '../../styles/_var'
import AboutCompetition from './AboutCompetition'
import CompetitionBanner from './CompetitionBanner'
import Text from '../../components/Text'
import Results from './Results'
import Discussions from './Discussions'
import DiscussionPage from './DiscussionPage'
import { capitalize, cleanEventChannel, isMobileScreen, wordify } from '../../utils/helpers'
import { TabItem } from '../../components/StyledComponents'
import contestantStore from '../../stores/contestantStore'
import VotingRoundOptions from './VotingRoundOptions'
import seasonStore from '../../stores/seasonStore'
import votingRoundStore from '../../stores/votingRoundStore'
import authStore from '../../stores/authStore'
import Aside from '../../components/Aside/Aside'
import CompetitionPageContestants from './CompetitionPageContestants'
import ErrorPage from '../ErrorPages/ErrorPage'
import { ADVERTISEMENTS, NOTES, POPULAR } from '../../components/Aside/constants'
import usePageTitle from '../../hooks/usePageTitle';
import VoteModal from './VoteModal';
import competitionPageStore, { 
  CONTESTANT_TAB,
  RESULT_TAB,
  DISCUSSION_TAB,
  VOTING_OPTIONS_TAB,
  routeNames 
} from '../../stores/competitionPageStore'
import { ICategory, TabType } from '../../utils/interfaces'
import { events, EventsChannel } from 'aws-amplify/data';
import { handleEvent } from '../../utils/eventHandlers'
import { useSubdomain } from '../../hooks/useSubdomain'


interface ICompetitionPageProps {
  className?: string
}

const extractPathComponents = (pathname: string) => pathname.match(/\/(\w*)\/(\w*)\/*/)

const connectToEventChannel = async (competitionUsername: string) => {
  const channelName = `/default/${cleanEventChannel(competitionUsername)}`
  console.log('Connected to event channel for competition:', channelName)
  const channel = await events.connect(channelName);
  channel.subscribe({
    next: handleEvent,
    error: (err) => console.error('error', err),
  });
  return channel
}

const CompetitionPage: FunctionComponent<ICompetitionPageProps> = () => {
  const theme = useTheme() 
  const isMediumScreenUp = useMediaQuery(theme.breakpoints.up('md'))
  const subdomain = useSubdomain()
  const { competition_id: c } = useParams<{ competition_id: string }>()
  const competition_id = c || subdomain

  const location = useLocation()
  const pathname = !!subdomain ? '' : `/${competition_id}`
  const [,,selectedTabOnLoad] = extractPathComponents(location.pathname) || []


  const season = seasonStore.selectedSeason
  const competitionUsername = competition_id
  const votingOptions = votingRoundStore.selectedVotingRound?.voting_option

  

  usePageTitle(competitionPageStore.data.competitionInfo.name)
  useEffect(() => {
    competitionPageStore.setCurrentTab(selectedTabOnLoad ? (capitalize(selectedTabOnLoad) as TabType) : CONTESTANT_TAB)
    return () => {competitionPageStore.clearCompetitionPageData()}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  
  useEffect(() => {
    if (competitionUsername) competitionPageStore.loadPageData(competitionUsername)
  }, [competitionUsername])

  useEffect(() => {
    if (!competitionUsername) return
    let channel: EventsChannel;

    connectToEventChannel(competitionUsername).then(c => {
      channel = c
    }).catch(err => {
      console.error('error', err)
    })
    return () => {channel?.close()}
  }, [competitionUsername])

  if (competitionPageStore.fetchingInfoFailed) return <ErrorPage code='404' />
  if (competitionPageStore.noCompetition) return null

  const renderCompetitionContestants = () => {
    return (
      <CompetitionPageContestants
        selectedCategory={competitionPageStore.data.selectedCategory}
        categories={competitionPageStore.competitionCategories}
        onSelectCategory={category => competitionPageStore.setSelectedCategory(category)}
        seasonConcluded={seasonStore.selectedSeason.concluded}
        userIsLoggedIn={authStore.userIsLoggedIn}
        votingClosed={votingRoundStore.lastVotingRound?.has_ended || !votingRoundStore.lastVotingRound?.has_started}
        onVote={contestantUsername => competitionPageStore.onVote(contestantUsername)}
        contestants={contestantStore.getContestantFilteredByCategory(competitionPageStore.data.selectedCategory)}
        showVoteCount={votingOptions?.show_vote_count}
        loading={contestantStore.fetchingContestants} 
        upcomingVotingRoundStarts={votingRoundStore.upcomingVotingRoundStarts ?? undefined}
      />
    )
  }

  return (
    <CompetitionPageWrapper role="main">
      <VoteModal 
        closeModal={competitionPageStore.closeModal}
        canShowVoteCount={competitionPageStore.showVoteCount}
        error={competitionPageStore.data.errorMessages.vote}
        onSubmitForm={() => competitionPageStore.onSubmitForm()}
        onInputChange={e => competitionPageStore.onVoteCountChange(+e.target.value)}
        voteCount={competitionPageStore.voteCount}
      />
      <Grid container spacing={isMobileScreen() ? 0 : 6}>
        <Grid item xs={12} sm={12} md={9}>
          <CompetitionBanner 
            {...competitionPageStore.data.competitionInfo} 
            seasons={seasonStore.seasons} 
            currentSeason={season}
            banner={season.banner}
            logo={season.logo}
            setSelectedSeason={seasonId => seasonStore.setSelectedSeasonById(seasonId)}
            showFavouriteIcon={authStore.userIsLoggedIn}
            onFavourite={() => competitionPageStore.toggleFavouriteCompetition()}
            isFavourite={competitionPageStore.isFavourite} />
          <AboutCompetition about={season.description} />
          <CompetitionBody role="tab">
            <TabSection role="tabpanel">
              {competitionPageStore.tabs.map((tab, index) => (
                <TabItem
                  onClick={() => competitionPageStore.setCurrentTab(tab)}
                  to={`${pathname}/${routeNames[tab]}`}
                  key={index}
                  selected={competitionPageStore.currentTab === tab}
                >
                  <Text variant="regular" truncate>{wordify(tab)}</Text>
                </TabItem>
              ))}
            </TabSection>
            <TabBody role="tablist">
              <Routes>
                <Route
                  path={`/${routeNames[RESULT_TAB]}`}
                  element={(
                    <Results 
                      selectedCategory={competitionPageStore.data.selectedCategory as ICategory}
                      categories={competitionPageStore.competitionCategories}
                      onSelectCategory={category => competitionPageStore.setSelectedCategory(category)}
                    />
                  )} 
                />
                <Route path={`/${routeNames[CONTESTANT_TAB]}`} element={renderCompetitionContestants()} />
                <Route
                  path={`/${routeNames[VOTING_OPTIONS_TAB]}`}
                  element={!!votingOptions ? <VotingRoundOptions {...votingOptions} /> : null} />
                <Route
                  path={`/${routeNames[DISCUSSION_TAB]}`}
                  element={<Discussions />} />
                <Route
                  path={`/${routeNames[DISCUSSION_TAB]}/:discussion_id`}
                  element={<DiscussionPage />} />
                <Route path='*' element={renderCompetitionContestants()} />
              </Routes>
            </TabBody>
          </CompetitionBody>
        </Grid>
        {isMediumScreenUp && (
          <Grid item md={3} sm={12} xs={12}>
            <Aside components={[NOTES, POPULAR, ADVERTISEMENTS]} />
          </Grid>
        )}
      </Grid>
    </CompetitionPageWrapper>
  )
}

export default observer(CompetitionPage)

export const CompetitionPageWrapper = styled(PageContent)`
  max-width: 1600px;
  margin-left: auto;
  margin-right: auto;
  margin-top: 8rem;
  padding-top: ${spacing.sm};
  margin-bottom: ${spacing.rg};
`

export const CompetitionBody = styled.div`
  margin-top: ${spacing.sm};
  border: solid 1px ${colors.lightgrey};
  border-radius: 1rem;
  min-height: 40rem;
`

export const TabSection = styled.div`
  display: flex;
  box-sizing: border-box;
  padding: ${spacing.xs} ${spacing.sm};
  justify-content: space-between;
`

export const TabBody = styled.div`
  box-sizing: border-box;
  padding: ${spacing.sm};
`

